import React from 'react';
import { FileText, Clock, CheckCircle, Camera, Upload } from 'lucide-react';

interface InventoryLoadingModalProps {
  isOpen: boolean;
  loadingStage?: 'loading' | 'processing' | 'complete' | 'uploading-photos';
  mode?: 'inventory' | 'photos'; // New prop to distinguish between inventory loading and photo uploads
}

const InventoryLoadingModal: React.FC<InventoryLoadingModalProps> = ({ 
  isOpen, 
  loadingStage = 'loading',
  mode = 'inventory' 
}) => {
  if (!isOpen) return null;

  const getStageContent = () => {
    // Photo upload mode
    if (mode === 'photos') {
      return {
        icon: <Upload className="w-8 h-8 text-blue-600 animate-pulse" />,
        title: 'Uploading Photos',
        description: 'Please wait while your photos are being processed and uploaded. You can continue using the app once complete.',
        progress: 'w-3/4',
        color: 'blue'
      };
    }
    
    // Inventory loading mode
    switch (loadingStage) {
      case 'loading':
        return {
          icon: <FileText className="w-8 h-8 text-teal-600 animate-pulse" />,
          title: 'Loading Inventory File',
          description: 'Fetching your inventory data...',
          progress: 'w-1/3',
          color: 'teal'
        };
      case 'processing':
        return {
          icon: <Clock className="w-8 h-8 text-teal-600 animate-spin" />,
          title: 'Processing Data',
          description: 'Organizing rooms, items, and photos...',
          progress: 'w-2/3',
          color: 'teal'
        };
      case 'complete':
        return {
          icon: <CheckCircle className="w-8 h-8 text-green-600" />,
          title: 'Almost Ready',
          description: 'Finalizing inventory display...',
          progress: 'w-full',
          color: 'green'
        };
      case 'uploading-photos':
        return {
          icon: <Camera className="w-8 h-8 text-blue-600 animate-pulse" />,
          title: 'Uploading Photos',
          description: 'Processing and uploading your photos...',
          progress: 'w-3/4',
          color: 'blue'
        };
      default:
        return {
          icon: <FileText className="w-8 h-8 text-teal-600 animate-pulse" />,
          title: 'Loading...',
          description: 'Please wait...',
          progress: 'w-1/4',
          color: 'teal'
        };
    }
  };

  const { icon, title, description, progress, color } = getStageContent();

  const colorClasses = {
    teal: {
      bg: 'bg-teal-50',
      gradient: 'from-teal-500 to-teal-600',
      dot: 'bg-teal-500'
    },
    blue: {
      bg: 'bg-blue-50',
      gradient: 'from-blue-500 to-blue-600',
      dot: 'bg-blue-500'
    },
    green: {
      bg: 'bg-green-50',
      gradient: 'from-green-500 to-green-600',
      dot: 'bg-green-500'
    }
  };

  const currentColor = colorClasses[color as keyof typeof colorClasses] || colorClasses.teal;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
        {/* Icon */}
        <div className="mb-6 flex justify-center">
          <div className={`p-4 ${currentColor.bg} rounded-full`}>
            {icon}
          </div>
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-gray-900 mb-3">
          {title}
        </h2>

        {/* Description */}
        <p className="text-gray-600 mb-8 text-lg">
          {description}
        </p>

        {/* Progress Bar */}
        <div className="w-full bg-gray-200 rounded-full h-3 mb-4 overflow-hidden">
          <div 
            className={`h-full bg-gradient-to-r ${currentColor.gradient} rounded-full transition-all duration-1000 ease-out ${progress}`}
          />
        </div>

        {/* Loading Animation */}
        <div className="flex justify-center space-x-2 mt-6">
          <div className={`w-2 h-2 ${currentColor.dot} rounded-full animate-bounce`} style={{ animationDelay: '0ms' }}></div>
          <div className={`w-2 h-2 ${currentColor.dot} rounded-full animate-bounce`} style={{ animationDelay: '150ms' }}></div>
          <div className={`w-2 h-2 ${currentColor.dot} rounded-full animate-bounce`} style={{ animationDelay: '300ms' }}></div>
        </div>

        {/* Additional Info */}
        <div className="mt-6 text-sm text-gray-500">
          {mode === 'photos' 
            ? 'Your photos are being compressed and uploaded to the cloud'
            : 'This may take a few moments for large inventories'
          }
        </div>
      </div>
    </div>
  );
};

export default InventoryLoadingModal;
